import type { AnalyticsService } from '@common/services';

import { LINE_LOAN_PURPOSE_OPTIONS, lineMaxAmount } from '../constants/credit-line.constants';
import type { CreditLine, CreditLineBlockReason, CreditLineRecalcRequest, CreditLineVersion, Credito, PayInstallmentVerification, PhoneChannel, PhonePurpose } from '../models';
import { CreditLineView, resolveCreditLineView } from './credit-line-view.util';

/**
 * Eventos PostHog de Fina Presta (FINA-3412). Sin Angular ni posthog: los componentes llaman a
 * `trackCreditEvent(this.analyticsService, '<clave>', props)`.
 *
 * - `CREDIT_EVENT_SPEC` es la única declaración de nombre, `feature` y props de cada evento en
 *   código; `CREDIT_EVENTS`, `CREDIT_EVENT_FEATURE` y `CREDIT_EVENT_PROPS` se derivan de él.
 * - `credit-analytics.catalog.json` documenta cada evento (pantalla, cuándo, una vez, embudos) y
 *   repite `feature` y props para `scripts/export-credit-events.js`, que corre en node plano. El
 *   spec exige que coincidan. No lo importes aquí: el util se carga al inicio (menú, Home, vigía) y
 *   el catálogo entraría al bundle principal.
 * - Los 24 nombres legacy no se renombran (rompería los insights de producción).
 * - Nunca ids, texto libre, cuenta, documento, referencia ni datos del comprobante: solo
 *   categorías y montos en USD. `trackCreditEvent` descarta toda prop que el registro no declare.
 * - Las claves de storage `cr_line_welcome_views_*`, `cr_line_done_seen_*`, `cr_line_known_level_*`,
 *   `cr_banner_hidden_*`, `cr_line_login_modal_checked` y `cr_eligible_modal_dismissed_*` NO son
 *   eventos.
 */

export type CreditEventFeature =
    | 'module'
    | 'calculator'
    | 'recalc'
    | 'request'
    | 'phone_verification'
    | 'home'
    | 'welcome'
    | 'due'
    | 'milestone'
    | 'nav'
    | 'loan'
    | 'payment'
    | 'abono'
    | 'blocked'
    | 'offer_v1';

export interface CreditEventDefinition {
    name: string;
    feature: CreditEventFeature;
    props: readonly string[];
}

/** Sustituye a `satisfies` (TS 4.5): un `feature` fuera de la unión no compila y se conservan las claves. */
const defineCreditEvents = <T extends Record<string, CreditEventDefinition>>(events: T): T => events;

/** Marcador del único nombre dinámico: `cr_dashboard_pagar_cuota_{n}_clicked`. */
export const CREDIT_EVENT_INSTALLMENT_PLACEHOLDER = '{n}';

export const CREDIT_ANALYTICS_MODULE = 'creditos';

const LINE_PROPS = ['line_version', 'line_status', 'block_reason', 'level', 'cupo_usd', 'cupo_limited_by', 'cupo_above_level_cap', 'can_request'] as const;
const CREDITO_PROPS = ['credito_status', 'credito_origin', 'installments_count'] as const;
const AMOUNT_PROPS = ['amount_usd', 'cupo_usd', 'is_max_amount', 'amount_pct_of_cupo'] as const;
const ERROR_PROPS = ['http_status', 'error_code', 'error_reason'] as const;
const PAYMENT_PROPS = ['pay_mode', 'installments_count', 'amount_usd', 'has_late_fee', 'payment_method', 'bank_code', 'correction_kind', 'line_mode', 'has_comprobante'] as const;
const PAYMENT_RESULT_PROPS = ['outcome', 'verification_error_code', 'attempts_left', 'settled_count'] as const;
const REQUEST_FORM_PROPS = ['bank_code', 'doc_type', 'ideal_amount_usd', 'ideal_is_custom', 'loan_purpose'] as const;
const DUE_BANNER_PROPS = ['variant', 'placement', 'days_overdue', 'days_to_due', 'pending_verification'] as const;

/** Sin duplicados: varios grupos comparten claves (`cupo_usd`, `installments_count`, `amount_usd`, `bank_code`). */
const props = (...groups: ReadonlyArray<readonly string[]>): readonly string[] => Array.from(new Set(groups.reduce<string[]>((all, group) => all.concat(group), [])));

export const CREDIT_EVENT_SPEC = defineCreditEvents({
    // Módulo
    moduleViewed: {
        name: 'cr_module_viewed',
        feature: 'module',
        props: props(LINE_PROPS, CREDITO_PROPS, ['recalc_status', 'view', 'is_owner', 'deposit_delayed', 'solicitud_open']),
    },
    moduleLoadFailed: { name: 'cr_module_load_failed', feature: 'module', props: ['stage', 'first_load', 'http_status'] },
    lineDepositDelayShown: { name: 'cr_line_deposit_delay_shown', feature: 'module', props: [] },

    // Calculadora
    linePedirClicked: { name: 'cr_line_pedir_clicked', feature: 'calculator', props: props(['amount', 'level'], AMOUNT_PROPS) },
    lineAmountSelected: { name: 'cr_line_amount_selected', feature: 'calculator', props: props(AMOUNT_PROPS, ['amount_source', 'rounded']) },
    lineCustomAmountOpened: { name: 'cr_line_custom_amount_opened', feature: 'calculator', props: AMOUNT_PROPS },
    lineCustomAmountDismissed: { name: 'cr_line_custom_amount_dismissed', feature: 'calculator', props: AMOUNT_PROPS },

    // Recálculo
    lineRecalcStarted: { name: 'cr_line_recalc_started', feature: 'recalc', props: ['field', 'recalc_status'] },
    lineRecalculoEnviado: { name: 'cr_line_recalculo_enviado', feature: 'recalc', props: ['length', 'requested_amount_usd', 'cupo_usd'] },
    lineRecalcFailed: { name: 'cr_line_recalc_failed', feature: 'recalc', props: ERROR_PROPS },

    // Solicitud
    lineRequestViewed: { name: 'cr_line_request_viewed', feature: 'request', props: props(AMOUNT_PROPS, ['level', 'installments_count']) },
    lineRequestRedirected: { name: 'cr_line_request_redirected', feature: 'request', props: ['redirect_reason', 'block_reason'] },
    lineRequestFieldCompleted: { name: 'cr_line_request_field_completed', feature: 'request', props: props(['field'], REQUEST_FORM_PROPS) },
    lineRequestContractOpened: { name: 'cr_line_request_contract_opened', feature: 'request', props: ['contract_outcome'] },
    lineSolicitudEnviada: {
        name: 'cr_line_solicitud_enviada',
        feature: 'request',
        props: props(['amount'], AMOUNT_PROPS, ['level', 'installments_count'], REQUEST_FORM_PROPS),
    },
    lineRequestFailed: { name: 'cr_line_request_failed', feature: 'request', props: props(ERROR_PROPS, ['amount_usd']) },
    lineRequestAbandoned: { name: 'cr_line_request_abandoned', feature: 'request', props: ['fields_completed', 'fields_completed_count', 'last_error_code'] },

    // Verificación del teléfono antes de pedir (ADM-813). Sin el número, la contraseña ni el código.
    phoneCodeSent: { name: 'cr_phone_code_sent', feature: 'phone_verification', props: ['channel', 'purpose'] },
    phoneCodeVerified: { name: 'cr_phone_code_verified', feature: 'phone_verification', props: ['channel', 'purpose'] },
    phoneChangeStarted: { name: 'cr_phone_change_started', feature: 'phone_verification', props: ['channel'] },

    // Home
    homeEligibleModalAutoOpened: { name: 'cr_home_eligible_modal_auto_opened', feature: 'home', props: CREDITO_PROPS },
    homeIrAPrestamosClicked: { name: 'cr_home_ir_a_prestamos_clicked', feature: 'home', props: [] },
    homeLineBannerViewed: { name: 'cr_home_line_banner_viewed', feature: 'home', props: ['kind', 'level'] },
    homeLineBannerClicked: { name: 'cr_home_line_banner_clicked', feature: 'home', props: ['kind', 'level'] },
    homeLineBannerDismissed: { name: 'cr_home_line_banner_dismissed', feature: 'home', props: ['kind', 'level'] },

    // Bienvenida
    lineWelcomeModalViewed: { name: 'cr_line_welcome_modal_viewed', feature: 'welcome', props: ['view_number', 'cycle_kind', 'level', 'cupo_usd'] },
    lineWelcomeModalPrimaryClicked: { name: 'cr_line_welcome_modal_primary_clicked', feature: 'welcome', props: ['level', 'cupo_usd'] },
    lineWelcomeModalDismissed: { name: 'cr_line_welcome_modal_dismissed', feature: 'welcome', props: ['dismiss_source', 'level', 'cupo_usd'] },

    // Banner de cuota
    dueBannerViewed: { name: 'cr_due_banner_viewed', feature: 'due', props: DUE_BANNER_PROPS },
    dueBannerDismissed: { name: 'cr_due_banner_dismissed', feature: 'due', props: DUE_BANNER_PROPS },

    // Modal de préstamo pagado / subida de nivel
    lineDoneModalViewed: { name: 'cr_line_done_modal_viewed', feature: 'milestone', props: ['variant', 'level', 'can_request', 'at_max_level', 'has_mark'] },
    lineDoneModalCtaClicked: { name: 'cr_line_done_modal_cta_clicked', feature: 'milestone', props: ['action', 'variant', 'level'] },
    lineDoneModalDismissed: { name: 'cr_line_done_modal_dismissed', feature: 'milestone', props: ['dismiss_source', 'variant', 'level'] },

    // Menú
    navFinaPrestaClicked: { name: 'cr_nav_fina_presta_clicked', feature: 'nav', props: ['has_late_credit'] },

    // Tu préstamo
    dashboardInstallmentSelected: {
        name: 'cr_dashboard_installment_selected',
        feature: 'loan',
        props: ['installment_number', 'installment_status', 'line_mode'],
    },
    dashboardPagarTodoClicked: { name: 'cr_dashboard_pagar_todo_clicked', feature: 'loan', props: ['line_mode'] },
    dashboardPagarCuota: { name: 'cr_dashboard_pagar_cuota_{n}_clicked', feature: 'loan', props: ['installment_number', 'line_mode'] },
    dashboardPagarVariasClicked: { name: 'cr_dashboard_pagar_varias_clicked', feature: 'loan', props: ['cuotas', 'line_mode'] },
    dashboardAbonarClicked: { name: 'cr_dashboard_abonar_clicked', feature: 'loan', props: ['cuota', 'line_mode'] },
    dashboardCorregirPagoClicked: { name: 'cr_dashboard_corregir_pago_clicked', feature: 'loan', props: ['tipo', 'cuotas', 'line_mode'] },

    // Modal de pago
    payModalOpened: { name: 'cr_pay_modal_opened', feature: 'payment', props: props(PAYMENT_PROPS, ['phase']) },
    pagoModalNotificarClicked: { name: 'cr_pago_modal_notificar_clicked', feature: 'payment', props: PAYMENT_PROPS },
    payModalClosed: { name: 'cr_pay_modal_closed', feature: 'payment', props: props(PAYMENT_PROPS, ['phase']) },
    paymentResult: { name: 'cr_payment_result', feature: 'payment', props: props(['flow'], PAYMENT_PROPS, PAYMENT_RESULT_PROPS) },
    paymentFailed: { name: 'cr_payment_failed', feature: 'payment', props: props(['flow'], PAYMENT_PROPS, ERROR_PROPS, ['rate_changed']) },

    // Modal de abono
    abonoModalOpened: { name: 'cr_abono_modal_opened', feature: 'abono', props: ['cuota', 'line_mode'] },
    abonoContinuarClicked: { name: 'cr_abono_continuar_clicked', feature: 'abono', props: ['cuota', 'amount', 'line_mode'] },
    abonoConfirmarClicked: { name: 'cr_abono_confirmar_clicked', feature: 'abono', props: ['cuota', 'amount', 'line_mode'] },
    abonoModalClosed: { name: 'cr_abono_modal_closed', feature: 'abono', props: ['step', 'line_mode'] },

    // Modal de bloqueo por mora
    blockedModalViewed: { name: 'cr_blocked_modal_viewed', feature: 'blocked', props: ['viewer_mode', 'pending_verification'] },
    pagoBloqueoNotificarClicked: { name: 'cr_pago_bloqueo_notificar_clicked', feature: 'blocked', props: [] },

    // Ofertas v1 (solo se centraliza el nombre)
    landingSolicitarClicked: { name: 'cr_landing_solicitar_clicked', feature: 'offer_v1', props: ['amount', 'maxAmount'] },
    eligibleVerPrestamoClicked: { name: 'cr_eligible_ver_prestamo_clicked', feature: 'offer_v1', props: [] },
    eligibleAhoraNoClicked: { name: 'cr_eligible_ahora_no_clicked', feature: 'offer_v1', props: [] },
    solicitudDatosCuentaCompletados: { name: 'cr_solicitud_datos_cuenta_completados', feature: 'offer_v1', props: [] },
    solicitudTerminosClicked: { name: 'cr_solicitud_terminos_clicked', feature: 'offer_v1', props: [] },
    solicitudMontoFuturoSeleccionado: { name: 'cr_solicitud_monto_futuro_seleccionado', feature: 'offer_v1', props: [] },
    solicitudMotivoSeleccionado: { name: 'cr_solicitud_motivo_seleccionado', feature: 'offer_v1', props: [] },
    solicitudEnviada: { name: 'cr_solicitud_enviada', feature: 'offer_v1', props: [] },
});

export type CreditEventKey = keyof typeof CREDIT_EVENT_SPEC;

const CREDIT_EVENT_KEYS = Object.keys(CREDIT_EVENT_SPEC) as CreditEventKey[];

const mapCreditEvents = <V>(pick: (def: CreditEventDefinition) => V): Record<CreditEventKey, V> =>
    CREDIT_EVENT_KEYS.reduce((acc, key) => {
        acc[key] = pick(CREDIT_EVENT_SPEC[key]);
        return acc;
    }, {} as Record<CreditEventKey, V>);

/** Clave → nombre del evento en PostHog. */
export const CREDIT_EVENTS: Record<CreditEventKey, string> = mapCreditEvents(def => def.name);
export const CREDIT_EVENT_FEATURE: Record<CreditEventKey, CreditEventFeature> = mapCreditEvents(def => def.feature);
/** Props permitidas por evento, además de `module` y `feature`. */
export const CREDIT_EVENT_PROPS: Record<CreditEventKey, readonly string[]> = mapCreditEvents(def => def.props);

// El global que Angular pone en desarrollo; el build de producción lo reemplaza por `false`.
declare const ngDevMode: unknown;

/** Modo desarrollo, en un objeto para que el spec cubra los dos valores con `spyOn`. */
export const CREDIT_ANALYTICS_DEV = {
    enabled: (): boolean => typeof ngDevMode !== 'undefined' && !!ngDevMode,
};

/**
 * Emite un evento de Fina Presta: resuelve el nombre dinámico, descarta las props que el registro
 * no declara (en desarrollo avisa por consola) y suma `module` y `feature` al final, para que no se
 * puedan pisar. La analítica nunca rompe el flujo de la pantalla.
 */
export function trackCreditEvent(analytics: Pick<AnalyticsService, 'analyticsCapture'> | null | undefined, key: CreditEventKey, props: object = {}): void {
    try {
        const eventProps = props as Record<string, unknown>;
        const def: CreditEventDefinition = CREDIT_EVENT_SPEC[key];
        let name = def.name;
        if (name.includes(CREDIT_EVENT_INSTALLMENT_PLACEHOLDER)) {
            const n = eventProps.installment_number;
            if (typeof n !== 'number' || !Number.isInteger(n) || n < 1) return;
            name = name.replace(CREDIT_EVENT_INSTALLMENT_PLACEHOLDER, String(n));
        }
        const allowed: Record<string, unknown> = {};
        for (const prop of Object.keys(eventProps)) {
            if (def.props.includes(prop)) {
                allowed[prop] = eventProps[prop];
            } else if (CREDIT_ANALYTICS_DEV.enabled()) {
                console.warn(`[creditos analytics] ${name}: prop no declarada ${prop}`);
            }
        }
        analytics?.analyticsCapture(name, { ...allowed, module: CREDIT_ANALYTICS_MODULE, feature: def.feature });
    } catch {
        // La analítica nunca rompe el flujo.
    }
}

/** «Una vez por visita»: deduplica por instancia del componente (una visita = su vida). */
export class CreditEventOnce {
    private readonly seen = new Set<string>();

    /** `true` solo la primera vez que ve la clave. */
    first(key: string): boolean {
        if (this.seen.has(key)) return false;
        this.seen.add(key);
        return true;
    }
}

const finiteOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const intOrNull = (value: unknown): number | null => {
    const n = finiteOrNull(value);
    return n !== null && Number.isInteger(n) ? n : null;
};

const boolOrNull = (value: unknown): boolean | null => (typeof value === 'boolean' ? value : null);

const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | null => (typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : null);

/** Códigos del servidor (`CREDIT_LINE_UNAVAILABLE`, `BANK_REF_NOT_FOUND`…): nunca texto libre. */
const ERROR_CODE_RE = /^[A-Z0-9_]+$/;
const codeOrNull = (value: unknown): string | null => (typeof value === 'string' && ERROR_CODE_RE.test(value) ? value : null);

/** Código SUDEBAN de 4 dígitos: nunca el nombre del banco ni otro texto. */
const bankCodeOrNull = (value: unknown): string | null => (typeof value === 'string' && /^\d{4}$/.test(value) ? value : null);

/** Exhaustivo por tipo: un motivo nuevo del servidor no compila hasta sumarse aquí. */
const KNOWN_BLOCK_REASONS: Record<Exclude<CreditLineBlockReason, null>, true> = {
    not_eligible: true,
    suspended: true,
    written_off: true,
    below_floor: true,
    open_credit: true,
    daily_limit: true,
    recalc_pending: true,
};

export interface CreditLineAnalyticsProps {
    line_version: CreditLineVersion | null;
    line_status: CreditLine['status'] | null;
    block_reason: CreditLineBlockReason;
    level: number | null;
    cupo_usd: number | null;
    cupo_limited_by: CreditLine['cupo']['limitedBy'] | null;
    cupo_above_level_cap: boolean | null;
    can_request: boolean | null;
}

/** Línea v2. `version` por defecto: `v2` con línea y `null` sin ella (la v1 la pasa quien la conoce). */
export function buildLineProps(line: CreditLine | null | undefined, version?: CreditLineVersion | null): CreditLineAnalyticsProps {
    const amount = finiteOrNull(line?.cupo?.amountUSD);
    const cap = finiteOrNull(line?.cupo?.capUSD);
    return {
        line_version: version !== undefined ? version : line ? 'v2' : null,
        line_status: line?.status ?? null,
        block_reason: line?.blockReason ?? null,
        level: finiteOrNull(line?.level),
        cupo_usd: amount,
        cupo_limited_by: line?.cupo?.limitedBy ?? null,
        // Sobre el tope del nivel: piso V1 o monto manual.
        cupo_above_level_cap: amount !== null && cap !== null ? amount > cap : null,
        can_request: line ? line.canRequest === true : null,
    };
}

export interface CreditoAnalyticsProps {
    credito_status: Credito['status'] | null;
    credito_origin: Credito['origin'] | null;
    installments_count: number | null;
}

export function buildCreditoProps(credito: Pick<Credito, 'status' | 'origin' | 'installments' | 'installmentsCount'> | null | undefined): CreditoAnalyticsProps {
    const fromSchedule = credito?.installments?.length || null;
    return {
        credito_status: credito?.status ?? null,
        credito_origin: credito?.origin ?? null,
        installments_count: fromSchedule ?? intOrNull(credito?.installmentsCount),
    };
}

export interface CreditAmountAnalyticsProps {
    amount_usd: number | null;
    cupo_usd: number | null;
    is_max_amount: boolean | null;
    amount_pct_of_cupo: number | null;
}

/** `is_max_amount`: el monto es el máximo pedible (`lineMaxAmount`, el cupo en múltiplos de $50). */
export function buildAmountProps(amountUSD: number | null | undefined, cupoUSD: number | null | undefined): CreditAmountAnalyticsProps {
    const amount = finiteOrNull(amountUSD);
    const cupo = finiteOrNull(cupoUSD);
    const max = cupo !== null ? lineMaxAmount(cupo) : null;
    return {
        amount_usd: amount,
        cupo_usd: cupo,
        is_max_amount: amount !== null && max !== null ? amount === max : null,
        amount_pct_of_cupo: amount !== null && cupo !== null && cupo > 0 ? Math.round((amount / cupo) * 100) : null,
    };
}

export type CreditRecalcStatus = 'none' | 'open' | 'answered' | 'cooldown';

/** `cooldown`: respondida y todavía antes de `nextAllowedAt` (igual que `line-recalc.isCoolingDown`). */
export function buildRecalcProps(recalc: Pick<CreditLineRecalcRequest, 'status' | 'nextAllowedAt'> | null | undefined, now: Date = new Date()): { recalc_status: CreditRecalcStatus | null } {
    if (!recalc) return { recalc_status: 'none' };
    if (recalc.status === 'open') return { recalc_status: 'open' };
    if (recalc.status !== 'answered') return { recalc_status: null };
    const next = recalc.nextAllowedAt ? new Date(recalc.nextAllowedAt).getTime() : NaN;
    return { recalc_status: Number.isFinite(next) && now.getTime() < next ? 'cooldown' : 'answered' };
}

export interface CreditErrorAnalyticsProps {
    http_status: number | null;
    error_code: string | null;
    error_reason: Exclude<CreditLineBlockReason, null> | null;
}

/**
 * Error de un request de créditos. La línea manda `code` (y `reason` en `CREDIT_LINE_UNAVAILABLE`);
 * pagos y abonos mandan `errorCode`. Nunca `title` ni `message`.
 */
export function buildCreditErrorProps(err: any): CreditErrorAnalyticsProps {
    const body = err?.error;
    const code = typeof body?.code === 'string' ? body.code : body?.errorCode;
    const status = Number(err?.status);
    const reason = body?.reason;
    return {
        http_status: Number.isInteger(status) && status > 0 ? status : null,
        error_code: codeOrNull(code),
        error_reason: typeof reason === 'string' && Object.prototype.hasOwnProperty.call(KNOWN_BLOCK_REASONS, reason) ? (reason as Exclude<CreditLineBlockReason, null>) : null,
    };
}

export interface CreditPaymentAnalyticsInput {
    mode?: string | null;
    installmentsCount?: number | null;
    amountUSD?: number | null;
    hasLateFee?: boolean | null;
    paymentMethod?: string | null;
    bankCode?: string | null;
    correctionKind?: string | null;
    lineMode?: boolean | null;
    hasComprobante?: boolean | null;
}

export interface CreditPaymentAnalyticsProps {
    pay_mode: 'one' | 'many' | 'all' | null;
    installments_count: number | null;
    amount_usd: number | null;
    has_late_fee: boolean | null;
    payment_method: 'pago_movil' | 'transferencia' | null;
    bank_code: string | null;
    correction_kind: 'cuota' | 'abono' | null;
    line_mode: boolean | null;
    has_comprobante: boolean | null;
}

/** Pago, abono o desbloqueo. Nunca referencia, teléfono, cuenta ni datos del comprobante. */
export function buildPaymentProps(input: CreditPaymentAnalyticsInput | null | undefined): CreditPaymentAnalyticsProps {
    return {
        pay_mode: oneOf(input?.mode, ['one', 'many', 'all'] as const),
        installments_count: intOrNull(input?.installmentsCount),
        amount_usd: finiteOrNull(input?.amountUSD),
        has_late_fee: boolOrNull(input?.hasLateFee),
        payment_method: oneOf(input?.paymentMethod, ['pago_movil', 'transferencia'] as const),
        bank_code: bankCodeOrNull(input?.bankCode),
        correction_kind: oneOf(input?.correctionKind, ['cuota', 'abono'] as const),
        line_mode: boolOrNull(input?.lineMode),
        has_comprobante: boolOrNull(input?.hasComprobante),
    };
}

export interface CreditPaymentResultAnalyticsProps {
    outcome: PayInstallmentVerification['outcome'] | null;
    verification_error_code: string | null;
    attempts_left: number | null;
    settled_count: number | null;
}

/** Resultado de la verificación bancaria (`res.verification`). Nunca `title`, `message` ni `caseId`. */
export function buildPaymentResultProps(
    verification: Partial<Pick<PayInstallmentVerification, 'outcome' | 'errorCode' | 'attemptsLeft' | 'settled'>> | null | undefined,
): CreditPaymentResultAnalyticsProps {
    const settled = verification?.settled;
    return {
        outcome: oneOf(verification?.outcome, ['verified', 'failed', 'manual_review', 'pending_manual'] as const),
        verification_error_code: codeOrNull(verification?.errorCode),
        attempts_left: intOrNull(verification?.attemptsLeft),
        settled_count: Array.isArray(settled) ? settled.length : null,
    };
}

export interface CreditRequestFormAnalyticsInput {
    bankCode?: string | null;
    docType?: string | null;
    idealAmountUSD?: number | null;
    idealIsCustom?: boolean | null;
    loanPurpose?: string | null;
}

export interface CreditRequestFormAnalyticsProps {
    bank_code: string | null;
    doc_type: 'V' | 'E' | 'J' | 'P' | 'G' | null;
    ideal_amount_usd: number | null;
    ideal_is_custom: boolean | null;
    loan_purpose: (typeof LINE_LOAN_PURPOSE_OPTIONS)[number]['value'] | null;
}

const LOAN_PURPOSES = LINE_LOAN_PURPOSE_OPTIONS.map(o => o.value);

/** Solicitud de la línea: solo categorías. Nunca el número de documento, la cuenta ni el texto de «Otros». */
export function buildRequestFormProps(input: CreditRequestFormAnalyticsInput | null | undefined): CreditRequestFormAnalyticsProps {
    return {
        bank_code: bankCodeOrNull(input?.bankCode),
        doc_type: oneOf(input?.docType, ['V', 'E', 'J', 'P', 'G'] as const),
        ideal_amount_usd: finiteOrNull(input?.idealAmountUSD),
        ideal_is_custom: boolOrNull(input?.idealIsCustom),
        loan_purpose: oneOf(input?.loanPurpose, LOAN_PURPOSES),
    };
}

export interface CreditPhoneChannelAnalyticsProps {
    channel: PhoneChannel | null;
}

export interface CreditPhoneCodeAnalyticsProps extends CreditPhoneChannelAnalyticsProps {
    purpose: PhonePurpose | null;
}

const PHONE_CHANNELS: readonly PhoneChannel[] = ['sms', 'whatsapp'];
const PHONE_PURPOSES: readonly PhonePurpose[] = ['verify', 'change'];

/** `cr_phone_change_started` (ADM-813): solo el canal elegido. Nunca el número ni la contraseña. */
export function buildPhoneChannelProps(channel: unknown): CreditPhoneChannelAnalyticsProps {
    return { channel: oneOf(channel, PHONE_CHANNELS) };
}

/** `cr_phone_code_sent` / `cr_phone_code_verified` (ADM-813): canal y propósito. Nunca el número ni el código. */
export function buildPhoneCodeProps(channel: unknown, purpose: unknown): CreditPhoneCodeAnalyticsProps {
    return { ...buildPhoneChannelProps(channel), purpose: oneOf(purpose, PHONE_PURPOSES) };
}

export type CreditModuleView = CreditLineView | 'locked_non_owner' | `offer_${Credito['status'] | 'none'}`;

/** Lo que pinta el módulo: la vista v2 del dueño, el candado del no dueño o la oferta v1 por estado. */
export function creditModuleView(isV2: boolean, isOwner: boolean, credito: Pick<Credito, 'status'> | null | undefined): CreditModuleView {
    if (isV2) return isOwner ? resolveCreditLineView(credito) : 'locked_non_owner';
    return `offer_${credito?.status ?? 'none'}`;
}
